<script setup>
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type } = defineProps({
    type: {
        type: String,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'price',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prix',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.price,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'minValue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur min',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.minValue,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'maxValue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur max',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.maxValue,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.modules.modulePackage',
                                    {
                                        modulePackage: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.modules.postModulePackages'),
    columnPinning: {
        right: ['actions'],
    },
    data: {
        type,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="route('front.projectManagement.modules.modulePackage')"
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
